import { z } from "zod";
import { zDiaCivil, zId } from "./dominio.js";
import { zOfensiva } from "./ofensiva.js";
import { diasEntre, somarDias } from "./dia.js";
/**
 * Estatísticas (tela 6 do SPEC).
 *
 * Duas fontes, de propósito. O que é **por dia** — volume e taxa — vem do
 * `diario`, a mesma derivada que a home e o heatmap leem: se a estatística
 * recontasse `resposta`, as duas telas poderiam discordar sobre o mesmo dia. O
 * que **não é por dia** — tempo médio e acerto por categoria — não cabe no
 * `diario`, que só guarda contagem, e sai de `resposta` com os índices que já
 * existem.
 *
 * Nenhuma janela de tempo está escrita aqui: quais são oferecidas e qual é a
 * padrão vêm da configuração (invariante 4).
 */
export const zConsultaDeEstatisticas = z.object({
    /** Quantos dias a janela cobre, contando hoje. Validada contra a config. */
    janelaDias: z.number().int().min(1).max(1825),
});
export const zDiaDaSerie = z.object({
    data: zDiaCivil,
    respondidos: z.number().int().nonnegative(),
    acertos: z.number().int().nonnegative(),
});
export const zLinhaDeCategoria = z.object({
    categoriaId: zId,
    caminho: z.array(z.string()),
    respondidos: z.number().int().nonnegative(),
    acertos: z.number().int().nonnegative(),
});
export const zPainelDeEstatisticas = z.object({
    hoje: zDiaCivil,
    /** Primeiro dia da janela. A série vem completa entre `de` e `hoje`. */
    de: zDiaCivil,
    janelaDias: z.number().int().positive(),
    respondidos: z.number().int().nonnegative(),
    acertos: z.number().int().nonnegative(),
    /** `null` quando não houve resposta nenhuma: não existe taxa de zero itens. */
    tempoMedioMs: z.number().int().nonnegative().nullable(),
    ofensiva: zOfensiva,
    /** Um item por dia da janela, inclusive os dias vazios. */
    serie: z.array(zDiaDaSerie),
    /** Ordenada por volume, não por acerto: categoria de 2 itens não lidera. */
    porCategoria: z.array(zLinhaDeCategoria),
    /** Quantas respostas a janela não conta por falta de item vivo na trilha. */
    itensArquivadosIgnorados: z.number().int().nonnegative(),
    /**
     * Quantas respostas da janela ficaram fora do tempo médio por passarem do
     * teto (RN-65). Elas continuam no volume e no acerto: o que o teto corta é a
     * medição de tempo, e a tela precisa poder dizer isso em vez de mostrar uma
     * média que emagreceu sem explicação.
     */
    temposIgnorados: z.number().int().nonnegative(),
});
/**
 * A taxa de acerto. `null` sem denominador — e não zero, que mentiria dizendo
 * que o dia foi ruim quando ele foi apenas vazio.
 */
export function taxaDeAcerto(acertos, respondidos) {
    if (respondidos <= 0)
        return null;
    return acertos / respondidos;
}
/**
 * Completa a janela com os dias que o `diario` não tem. Dia sem registro é dia
 * sem estudo, e ele precisa aparecer no gráfico: um buraco silencioso faria
 * duas semanas de abandono parecerem duas semanas apertadas.
 */
export function preencherJanela(de, ate, dias) {
    const porData = new Map(dias.map((d) => [d.data, d]));
    const total = diasEntre(de, ate);
    if (total < 0)
        return [];
    const serie = [];
    for (let i = 0; i <= total; i++) {
        const data = somarDias(de, i);
        serie.push(porData.get(data) ?? { data, respondidos: 0, acertos: 0 });
    }
    return serie;
}
/**
 * A evolução da taxa (SPEC tela 6).
 *
 * Duas linhas, porque uma só engana. A do dia é ruidosa — dez itens fazem a
 * taxa pular de 60% para 90% sem nada ter mudado. A acumulada é estável mas
 * lenta, e esconde uma virada recente. Juntas dizem a verdade.
 *
 * Não há média móvel, de propósito: o tamanho da janela dela seria um número de
 * negócio escolhido no escuro (invariante 4), e a acumulada resolve o mesmo
 * problema sem inventar parâmetro.
 */
export function evolucaoDaTaxa(serie) {
    let acertos = 0;
    let respondidos = 0;
    return serie.map((d) => {
        acertos += d.acertos;
        respondidos += d.respondidos;
        return {
            data: d.data,
            taxaDoDia: taxaDeAcerto(d.acertos, d.respondidos),
            taxaAcumulada: taxaDeAcerto(acertos, respondidos),
        };
    });
}
/** O total da janela, para a faixa de métricas do topo. */
export function resumirSerie(serie) {
    const respondidos = serie.reduce((s, d) => s + d.respondidos, 0);
    const acertos = serie.reduce((s, d) => s + d.acertos, 0);
    return { respondidos, acertos, taxa: taxaDeAcerto(acertos, respondidos) };
}
/**
 * Ordena o acerto por categoria: mais respondidas primeiro, e o nome desempata
 * para a lista não dançar entre duas leituras iguais.
 */
export function ordenarCategorias(linhas) {
    return [...linhas].sort((a, b) => b.respondidos - a.respondidos || a.caminho.join(" › ").localeCompare(b.caminho.join(" › ")));
}
//# sourceMappingURL=estatistica.js.map